import { SPREADSHEET_MIN_DIMENSION } from "@/constants";
import { useWorkbookId } from "@/hooks/use-workbook";
import queries, { ssQueries } from "@/queries";
import { CellType } from "@/stores/mapping";
import { useQueries, useQuery } from "@tanstack/react-query";
import { MetaInformation, WorksheetOptions } from "jspreadsheet-ce";

import { getColumns, isNoteCategory, isParagraphOnlySheet } from "./columns";

const useCellsData = (workbookId: string, categoryId: string | null) => {
  const { data: categoryData } = useQuery({
    ...queries.workbook(workbookId!)._ctx.categories(),
    enabled: !!workbookId && !!categoryId,
    select: (data) => {
      return data.categories.find((category) => category.id === categoryId);
    },
  });

  const { data: spreadsheetsData, isLoading: spreadsheetsLoading } = useQuery({
    ...ssQueries.spreadsheets(workbookId!)._ctx.category(),
    enabled: !!workbookId,
  });

  const allTableIds =
    spreadsheetsData?.spreadsheets?.map((spreadsheet) => spreadsheet.tableId) ||
    [];

  const tableIds =
    allTableIds.filter((tableId) =>
      categoryData?.tables.some((table) => table.id === tableId),
    ) || [];

  const cellsResults = useQueries({
    queries: tableIds.map((tableId) => ({
      ...queries.workbook(workbookId!)._ctx.tables(tableId!)._ctx.cells(),
      enabled: !!workbookId && !!tableId,
    })),
  });

  return {
    data: cellsResults,
    tableIds,
    category: categoryData?.category,
    isLoading:
      spreadsheetsLoading || cellsResults.some((result) => result.isLoading),
    isError: cellsResults.some((result) => result.isError),
  };
};

export const useSpreadsheetData = (categoryId: string | null) => {
  const { data: workbookId } = useWorkbookId();
  const {
    data: cellsResults,
    isLoading: cellsLoading,
    isError: cellsError,
    tableIds,
    category,
  } = useCellsData(workbookId!, categoryId);
  const noteCategory = isNoteCategory(category);

  const { data, isLoading, isError } = useQuery({
    ...ssQueries.spreadsheets(workbookId!)._ctx.category(),
    enabled: !!workbookId && !cellsLoading,
    select: (data) => {
      if (!data.spreadsheets) return [] as WorksheetOptions[];

      const sortedSpreadsheets = [...data.spreadsheets].sort((a, b) =>
        (a.sheetName ?? "").localeCompare(b.sheetName ?? "", "ko", {
          numeric: true,
          sensitivity: "base",
        }),
      );

      const worksheetOptions = sortedSpreadsheets.map((spreadsheet, index) => {
        const tableIndex = tableIds.findIndex(
          (id) => id === spreadsheet.tableId,
        );
        const cellsData =
          tableIndex >= 0 ? cellsResults[tableIndex]?.data : undefined;

        const defRoleSuffixes = new Set<string>();

        const classes: Record<string, string> = {};
        if (cellsData?.cells) {
          cellsData.cells.forEach((cell) => {
            if (cell.taggedInfos.roleKey) {
              const suffix =
                cell.taggedInfos.roleKey.split("_").pop() ??
                cell.taggedInfos.roleKey;
              defRoleSuffixes.add(suffix);
            }

            // SECTION/TEXTBLOCK 은 role 배경색을 칠하지 않는다 (DATA/SUM 만).
            if (cell.type !== CellType.DATA && cell.type !== CellType.SUM) {
              return;
            }

            if (cell.taggedInfos.roleKey) {
              const suffix =
                cell.taggedInfos.roleKey.split("_").pop() ??
                cell.taggedInfos.roleKey;
              classes[cell.address] =
                `definition-cell-${Array.from(defRoleSuffixes).indexOf(suffix)}`;
            }
          });
        }

        const sheetData = spreadsheet.data || [[]];
        const sheetMergeCells = spreadsheet.mergeCells || {};
        // 문단이 넓혀져 있을 때만 wrap 을 켠다. 백엔드의 문단 병합 이전에 분석된
        // 워크북은 문단이 좁은 A열에 그대로 남아 있어, wrap 을 켜면 오히려 더 길게
        // 감긴다. 표 없는 주석은 병합 대상이 아니지만 A열을 600px 로 펴므로 켠다.
        const wordWrap =
          noteCategory &&
          (isParagraphOnlySheet(sheetData, sheetMergeCells) ||
            (cellsData?.paragraphMerged ?? false));

        return {
          worksheetId: spreadsheet.sheetId,
          worksheetName: spreadsheet.sheetName,
          data: sheetData,
          minDimensions: [
            Math.max(
              SPREADSHEET_MIN_DIMENSION[0],
              spreadsheet.data?.[0]?.length || 6,
            ),
            Math.max(
              SPREADSHEET_MIN_DIMENSION[1],
              spreadsheet.data?.length || 20,
            ),
          ] as [number, number],
          mergeCells: sheetMergeCells,
          tableOverflow: true,
          textOverflow: false,
          wordWrap,
          columns: getColumns(
            sheetData,
            spreadsheet.style || {},
            sheetMergeCells,
          ),
          style: spreadsheet.style || {},
          meta: {
            info: {
              workbookId: workbookId,
              categoryId: spreadsheet.categoryId,
              tableId: spreadsheet.tableId,
            } as MetaInformation,
          },
          classes,
        } as WorksheetOptions;
      });

      if (worksheetOptions.length === 0) {
        const uuid = crypto.randomUUID();

        return [
          {
            worksheetId: uuid,
            worksheetName: "Sheet1",
            categoryId: "",
            data: [[]],
            minDimensions: SPREADSHEET_MIN_DIMENSION,
            mergeCells: {},
            style: {},
            meta: {
              info: {
                categoryId: "",
                workbookId: workbookId,
                tableId: "",
              } as MetaInformation,
            },
          } as WorksheetOptions,
        ];
      }
      return worksheetOptions;
    },
  });

  return {
    data: data || [],
    isLoading: !(
      tableIds.length > 0 &&
      tableIds.length ===
        cellsResults.filter((result) => result.data !== undefined).length
    ),
    isError: isError || cellsError,
    cellsData: cellsResults.map((result) => result.data), // Return all cells data separately if needed
  };
};
